import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, swingsPastRest } from '../skillUsage';

export const followThroughOverlapSkill: CodeoSkill = {
    name: SKILL_NAMES.followThroughOverlap,
    kind: 'codeo',
    description:
        'When the body of an object stops, its loose parts keep going: they drag behind while it ' +
        'travels, swing past when it stops, and wobble back and forth, smaller each time, before ' +
        'they settle.',
    checkLooksFor:
        'a lean, bend or skew that swings back and forth past rest at least three times (keyframes like -DRAG, DRAG, -0.73 * DRAG, …)',
    documentUsesSkill: check((script) => swingsPastRest(script)),
    instructions: `# Follow Through and Overlap

When the body of an object stops, the loose parts keep going. A rigid object stops dead and reads as stiff. A flexible one **drags behind** while it travels: its loose end trails the body. When the body brakes, the loose end **swings past** (follow through), and then it **wobbles back and forth, a little less each time**, settling after the body is already still (overlapping action). The body's move is the same either way; all the life is in the part that lags.

## The numbers

Measured on a flexible bar three times as tall as it is wide, crossing about three-quarters of a frame in 0.75s:
- **Drag builds through the move and lags it.** The tip leans back further as the body speeds up and keeps leaning while the body is already slowing. It reaches about **35°** a half-swing before the stop, rising quickly at first and then flattening (\`power2.out\`).
- **The swing-through happens at the stop.** As the body brakes, the tip whips from 35° behind to **35° ahead**, landing on the instant the body comes to rest.
- **Then a damped wobble.** Half-swings of about **0.17s**, each about 0.7 of the last: peaks of 1, 0.73, 0.5 and 0.23 of the drag, then rest. It is still about 0.7s after the body stopped.
- **The bend is a smooth arc, not a hinge.** The part curves evenly along its length. The base stays planted and flat, and the tip stays square to the curve.
- **The body's move brakes decisively** over its last fifth. A body that drifts gently to a stop gives the tip nothing to swing past.

## Markup

The body moves; the flexible part inside it is one stroked SVG path, re-drawn as an arc of constant curvature.

\`\`\`html
<div id="ft-stage" style="position: absolute; inset: 0; display: grid; place-items: center; overflow: hidden;">
  <div id="ft-body" style="grid-area: 1 / 1;">
    <svg id="ft-svg" width="300" height="300" viewBox="-150 -240 300 300" overflow="visible">
      <path id="ft-stem" d="M0,0 L0,-210" fill="none" stroke="var(--l5-color-0, #101820)" stroke-width="70" stroke-linecap="butt" />
    </svg>
  </div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });

CustomEase.create('slideStop', 'M0,0 C0.556,0.011 0.62,1 1,1');

const STOP = 1.2;        // the body comes to rest: the word it lands on
const SLIDE = 0.75;
const LENGTH = 210;      // the flexible part, base to tip
const DRAG = 35;         // degrees the tip trails at full speed
const SWING = 0.17;      // one half-swing, peak to peak

// One arc of constant curvature: base planted, tip square to the curve.
function bentStem(degrees) {
    const angle = (degrees * Math.PI) / 180;
    if (Math.abs(angle) < 1e-4) return \`M0,0 L0,\${-LENGTH}\`;
    const radius = LENGTH / Math.abs(angle);
    const tipX = Math.sign(angle) * radius * (1 - Math.cos(angle));
    const tipY = -radius * Math.sin(Math.abs(angle));
    return \`M0,0 A\${radius},\${radius} 0 0 \${angle > 0 ? 1 : 0} \${tipX},\${tipY}\`;
}
const stem = document.querySelector('#ft-stem');
const bend = { degrees: 0 };

// The body: one move, braking hard over its last fifth.
tl.fromTo('#ft-body', { x: -1100 }, { x: 0, duration: SLIDE, ease: 'slideStop' }, STOP - SLIDE);

// The loose part: drags behind, swings past at the stop, wobbles down to rest.
// Key only the extremes, and ease between them like a pendulum.
tl.to(bend, {
    keyframes: [
        { degrees: -DRAG, duration: SLIDE - SWING, ease: 'power2.out' },
        { degrees: DRAG, duration: SWING, ease: 'sine.inOut' },
        { degrees: -0.73 * DRAG, duration: SWING, ease: 'sine.inOut' },
        { degrees: 0.5 * DRAG, duration: SWING, ease: 'sine.inOut' },
        { degrees: -0.23 * DRAG, duration: SWING, ease: 'sine.inOut' },
        { degrees: 0, duration: SWING, ease: 'sine.out' },
    ],
    onUpdate: () => stem.setAttribute('d', bentStem(bend.degrees)),
}, STOP - SLIDE);

tl.set({}, {}, STOP + 1.2);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **A word.** Lean the whole word with \`skewX\` about its baseline (\`transformOrigin: '50% 100%'\`) on the same keyframes, but keep the peak to 14–18° so it stays readable. Positive \`skewX\` leans the top left, so a word moving right drags with positive values first.
- **Letters as loose parts.** Overlapping action without a bend: each letter arrives a little after the one before (0.03s apart), overshoots its slot and settles, so the word lands like a chain rather than a block.
- **Other loose parts.** A plant's stem or leaves, an antenna, a flag, a tail, a scarf, the string of a balloon, a tall thin shape. Anything rigid stays rigid: its stiffness is the contrast that makes the loose part read.
- **Length sets the tempo.** A longer part swings slower: twice the length is about 1.4× the half-swing. A short stub barely needs two swings.
- **A wind-up first.** If the body pulls back before it goes (\`${SKILL_NAMES.anticipationWindUp}\`), the tip drags the other way during the pull-back and passes straight through upright at the turnaround.

## Pitfalls

- **Never slow it to fill a scene.** The swing times are the part's stiffness: slowed, a stem becomes a reed waving underwater. However long the scene, the slide and the swings take these times and the part then stands still.
- **Key only the extremes.** Keyframes at every in-between value with an in-out ease make the part stop at each one and stutter. One key per peak, \`sine.inOut\` between them.
- **A wobble that does not shrink is jelly**, and one that shrinks too fast (a single overshoot) is a spring. Four swings at about 0.7 each, then rest.
- **No drag, no follow through.** A part that is upright the whole move and only wobbles after the stop looks like it was hit, not carried. The swing-through is the release of the drag.
- **A shear is not a bend.** Skewing a tall shape slides its top sideways like a parallelogram; a bend curves it. Use the arc for anything drawn and long; keep the skew for type.
- Don't move the whole element's position in the wobble. The body is still; only the loose part keeps going. A whole element bouncing at its resting spot is \`${SKILL_NAMES.springSettle}\`.`,
};
